import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";

// Import translation files from translations folder (outside i18n to avoid babel-plugin-formatjs)
/* eslint-disable global-require */
const en = require("../translations/en.js");
const ar = require("../translations/ar.js");
/* eslint-enable global-require */

// Constants
const LOCALE_COOKIE_NAME = "openedx-language-preference";
const LOCALE_STORAGE_KEY = "openedx-language-preference";
const DEFAULT_LOCALE = "en";

// Helper to get locale from cookie
const getLocaleFromCookie = () => {
  const match = document.cookie.match(
    new RegExp(`(?:^|; )${LOCALE_COOKIE_NAME}=([^;]*)`)
  );
  return match ? match[1] : null;
};

// Helper to get locale from localStorage
const getLocaleFromStorage = () => {
  try {
    return localStorage.getItem(LOCALE_STORAGE_KEY);
  } catch {
    return null;
  }
};

// Get initial locale
const getInitialLocale = () => {
  const cookieLocale = getLocaleFromCookie();
  const storageLocale = getLocaleFromStorage();

  // Normalize locale (platform might store 'ar' or 'ar-sa', we just need the prefix)
  const locale = cookieLocale || storageLocale || DEFAULT_LOCALE;
  return locale.split('-')[0]; // Return just 'en' or 'ar'
};

const LanguageContext = createContext(undefined);

export function LanguageProvider({ children }) {
  const [locale, setLocale] = useState(getInitialLocale);
  const isArabic = locale?.startsWith("ar") ?? false;

  // Apply RTL/LTR and lang attribute
  useEffect(() => {
    document.documentElement.dir = isArabic ? "rtl" : "ltr";
    document.documentElement.lang = isArabic ? "ar" : "en";
  }, [isArabic]);

  // Toggle language function
  const toggleLang = useCallback(() => {
    const newLang = isArabic ? "en" : "ar";

    // Clear any existing language cookie first
    document.cookie = `${LOCALE_COOKIE_NAME}=;path=/;max-age=0`;

    // Set cookie with all necessary attributes for production
    document.cookie = `${LOCALE_COOKIE_NAME}=${newLang}; path=/; max-age=31536000`;

    // Also save to localStorage as backup
    try {
      localStorage.setItem(LOCALE_STORAGE_KEY, newLang);
    } catch {
      // Silently fail if localStorage is not available
    }

    // Small delay to ensure cookie is saved before reload
    setTimeout(() => {
      window.location.reload();
    }, 100);
  }, [isArabic]);

  // Listen for cookie/storage changes
  useEffect(() => {
    const checkLocaleChange = () => {
      const newLocale = getInitialLocale();
      if (newLocale !== locale) {
        setLocale(newLocale);
      }
    };

    const interval = setInterval(checkLocaleChange, 1000);
    window.addEventListener("storage", checkLocaleChange);

    return () => {
      clearInterval(interval);
      window.removeEventListener("storage", checkLocaleChange);
    };
  }, [locale]);

  // Simple translation function - just pass the key string directly
  // Usage: t('header.menu') or t('header.menu', { name: 'John' })
  const t = useCallback(
    (key, vars) => {
      // Get text from current language, fallback to English
      const dict = isArabic ? ar : en;
      let text = dict[key] || en[key] || key;

      // No variables - return plain string
      if (!vars || Object.keys(vars).length === 0) {
        return text;
      }

      // Check if any values are React elements
      const varKeys = Object.keys(vars);
      const hasReactElements = varKeys.some((k) =>
        React.isValidElement(vars[k])
      );

      if (!hasReactElements) {
        // Simple string replacement
        let result = text;
        varKeys.forEach((varKey) => {
          result = result.split(`{${varKey}}`).join(String(vars[varKey]));
        });
        return result;
      }

      // Has React elements - split and interleave
      const parts = [];
      const regex = /\{([^}]+)\}/g;
      let lastIndex = 0;
      let match;

      while ((match = regex.exec(text)) !== null) {
        if (match.index > lastIndex) {
          parts.push(text.slice(lastIndex, match.index));
        }

        const varName = match[1];
        if (varName in vars) {
          const value = vars[varName];
          if (React.isValidElement(value)) {
            parts.push(
              React.cloneElement(value, {
                key: `${varName}-${match.index}`,
              })
            );
          } else {
            parts.push(String(value));
          }
        } else {
          parts.push(match[0]);
        }

        lastIndex = regex.lastIndex;
      }

      if (lastIndex < text.length) {
        parts.push(text.slice(lastIndex));
      }

      if (parts.length === 1 && typeof parts[0] === "string") {
        return parts[0];
      }

      return React.createElement(React.Fragment, null, ...parts);
    },
    [isArabic]
  );

  return (
    <LanguageContext.Provider value={{ locale, isArabic, t, toggleLang, ar, en }}>
      {children}
    </LanguageContext.Provider>
  );
}

// Hook to consume translations
export function useT() {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error("useT must be used within a LanguageProvider");
  }
  return ctx;
}

// Alias for convenience
export const useLang = useT;

// HOC for class components (similar to injectIntl)
export function withT(Component) {
  function WithT(props) {
    const { t, isArabic, toggleLang } = useT();
    return <Component {...props} t={t} isArabic={isArabic} toggleLang={toggleLang} />;
  }
  WithT.displayName = `withT(${Component.displayName || Component.name || "Component"})`;
  return WithT;
}

export default LanguageContext;
